// React & MUI
import React, { useMemo } from "react";
import { Stack } from "@mui/material";
import { SearchInput } from "components/Form";
import { StyledButton } from "customStyled";
import { PlusOutlined } from "@ant-design/icons";
import { OverflowTooltip } from "Reuse/UI";

// Custom Components

/**
 * PageSubTitle Component
 * ----------------------
 * Renders a subtitle section for pages, optionally including:
 * - a count label (e.g., "5 Districts")
 * - child elements (e.g., buttons, toggles)
 * - a search input
 *
 * Props:
 * - text (string): Label text for the subtitle
 * - count (number): Number to display before the label (default: 0)
 * - children (ReactNode): Additional elements to render on the right
 * - isSearch (boolean): Whether to display the search input (default: false)
 * - searchPlaceHolder (string): Placeholder text for search input
 * - isFilter (boolean): If true, allows wrapping of children (default: false)
 * - incluedS (boolean): If true, adds "s" for plural count (default: true)
 * - addButtonClick (function | null): Callback for Add button
 * - addButtonText (string): Text for Add button
 */
const PageSubTitle = (props) => {
  const {
    isFilter = false,
    children,
    count = 0,
    selectedCount = 0,
    text,
    isSearch = false,
    incluedS: includeS = true,
    incluedES: includeES = false,
    searchPlaceHolder = "",
    addButtonClick = null,
    addButtonText = "",
    showAddIcon = true,
    isFlexGrow = true,
  } = props;

  const title = useMemo(() => {
    const isSelected = selectedCount > 0;
    const latestCount = isSelected ? selectedCount : count;
    const plural =
      latestCount > 1 ? (includeES ? "es" : includeS ? "s" : "") : "";

    return `${latestCount} ${text}${plural}${isSelected ? " selected" : ""}`;
  }, [count, selectedCount, includeES, includeS, text]);

  return (
    <Stack
      direction={"row"}
      spacing={1}
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: isFilter ? "wrap" : "nowrap",
        padding: "20px 24px",
      }}
    >
      {/* Left section: count and text */}
      <OverflowTooltip sx={{ flexGrow: isFlexGrow ? 1 : 0 }}>
        {title}
      </OverflowTooltip>
      {/* Center / right section: children elements */}
      {children}

      {/* Optional search input */}
      {isSearch ? <SearchInput placeHolder={searchPlaceHolder} /> : null}
      {/* Conditional Add Button */}
      {addButtonClick ? (
        <StyledButton
          sx={{
            height: 44,
          }}
          variant="contained"
          startIcon={showAddIcon ? <PlusOutlined /> : null}
          onClick={addButtonClick}
        >
          {addButtonText}
        </StyledButton>
      ) : null}
    </Stack>
  );
};

// Memoize to prevent unnecessary re-renders
export default React.memo(PageSubTitle);
